import React, { useMemo } from "react";
import Icon from "../../../components/icon/icon";
import Table from "../../../components/table/table";
import { ITEMS_PER_PAGE_OPTIONS } from "../../../constants/pagination";
import { Brand } from "../../../types";
import { BrandTableColumn, BrandTableProps } from "../types/brands-types";
import { useEffectiveBranchId } from "../../../hooks/use-effective-branch-id";

export const BrandTable: React.FC<BrandTableProps> = ({
  brands,
  isLoading,
  currentPage,
  itemsPerPage,
  totalPages,
  onEdit,
  onDelete,
  onPageChange,
  onItemsPerPageChange,
}) => {
  const effectiveBranchId = useEffectiveBranchId();

  const filteredBrands = useMemo(() => {
    // If no branch is selected, show all brands
    if (!effectiveBranchId) return brands;

    // Check if any brand has branchId and if any brand lacks it
    const hasAnyBranch = brands.some((b) => !!b.branchId);
    const hasAnyNoBranch = brands.some((b) => !b.branchId);

    // If no brands have branchId, show all (server-side filtering handles it)
    if (!hasAnyBranch) return brands;

    // Warn about mixed branchId presence
    if (hasAnyBranch && hasAnyNoBranch) {
      console.warn(
        "Mixed branchId presence detected in brands array. Some brands have branchId while others don't."
      );
    }

    // Filter by branchId
    return brands.filter((brand) => brand.branchId === effectiveBranchId);
  }, [brands, effectiveBranchId]);

  const columns: BrandTableColumn[] = [
    {
      title: "Название",
      dataIndex: "name" as keyof Brand,
      key: "name",
    },
    {
      title: "Действия",
      key: "action",
      dataIndex: "id" as keyof Brand,
      render: (_: any, record: Brand) => (
        <div className="brands__actions">
          <button
            onClick={() => onEdit(record)}
            className="brands__action-btn"
            title="Редактировать"
          >
            <Icon name="pencil" size="small" />
          </button>
          <button
            onClick={() => onDelete(record)}
            className="brands__action-btn"
            title="Удалить"
          >
            <Icon name="delete" size="small" />
          </button>
        </div>
      ),
    },
  ];

  const handleItemsPerPageChange = (value: string): void => {
    onItemsPerPageChange(parseInt(value));
  };

  return (
    <div className="brands__table-container">
      <Table
        columns={columns}
        dataSource={filteredBrands}
        rowKey="id"
        loading={isLoading}
        emptyMessage="Бренды не найдены"
        currentPage={currentPage}
        itemsPerPage={itemsPerPage}
      />

      <div className="brands__pagination">
        <div className="brands__pagination-info">
          Показано{" "}
          {Math.min(
            (currentPage - 1) * itemsPerPage + 1,
            filteredBrands.length
          )}{" "}
          - {Math.min(currentPage * itemsPerPage, filteredBrands.length)} из{" "}
          {filteredBrands.length}
        </div>

        <div className="brands__pagination-controls">
          <select
            value={itemsPerPage}
            onChange={(e) => handleItemsPerPageChange(e.target.value)}
            className="brands__page-size-select"
          >
            {ITEMS_PER_PAGE_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option} на странице
              </option>
            ))}
          </select>

          <div className="brands__page-buttons">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="brands__page-btn"
            >
              <Icon name="left" size="small" />
            </button>

            <span className="brands__page-info">
              {currentPage} из {totalPages}
            </span>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="brands__page-btn"
            >
              <Icon name="right" size="small" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
